import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Alert,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  FormControl,
  FormLabel,
  Link as MuiLink,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import api from "../../services/api";

const REASONS = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "didnt_use_enough", label: "I didn't use it enough to justify the cost" },
  { value: "missing_features", label: "Missing features I need" },
  { value: "plans_didnt_fit", label: "The meal plans didn't fit my family" },
  { value: "found_alternative", label: "I found an alternative" },
  { value: "just_exploring", label: "I was just exploring" },
];

type PageState = "loading" | "form" | "error" | "already_submitted" | "thank_you";

const TrialSurvey: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [state, setState] = useState<PageState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setError("No survey token provided");
        setState("error");
        return;
      }

      try {
        await api.get(`/survey/verify?token=${token}`);
        setState("form");
      } catch (err: any) {
        const status = err.response?.status;
        if (status === 409) {
          setState("already_submitted");
        } else {
          setError(
            err.response?.data?.message || "This survey link is invalid or has expired."
          );
          setState("error");
        }
      }
    };

    verify();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      await api.post("/survey/submit", {
        token,
        reason,
        feedback: feedback.trim() || undefined,
      });
      setState("thank_you");
    } catch (err: any) {
      if (err.response?.status === 409) {
        setState("already_submitted");
      } else {
        setError(err.response?.data?.message || "Failed to submit survey");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "loading") {
    return (
      <Container maxWidth="sm">
        <Box mt={8} textAlign="center">
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading survey...</Typography>
        </Box>
      </Container>
    );
  }

  if (state === "error") {
    return (
      <Container maxWidth="sm">
        <Box mt={8}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  if (state === "already_submitted") {
    return (
      <Container maxWidth="sm">
        <Box mt={8}>
          <Alert severity="info">
            You've already submitted this survey. Thank you for your feedback!
          </Alert>
          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            <MuiLink href="https://snackdapp.com">Back to snackdapp.com</MuiLink>
          </Typography>
        </Box>
      </Container>
    );
  }

  if (state === "thank_you") {
    return (
      <Container maxWidth="sm">
        <Box mt={8}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Thank you for your feedback!
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your response helps us make Snack'd better for everyone. If you ever want to
            come back, your data is still waiting for you.
          </Typography>
          <Typography variant="body2" align="center">
            <MuiLink href="https://snackdapp.com">Back to snackdapp.com</MuiLink>
          </Typography>
        </Box>
      </Container>
    );
  }

  // state === "form"
  return (
    <Container maxWidth="sm">
      <Box mt={6} mb={4}>
        <Typography variant="h4" gutterBottom>
          We'd love your feedback
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your trial has ended, but your input can help us improve Snack'd for
          families everywhere. This only takes 30 seconds.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <FormControl component="fieldset" sx={{ mb: 3, width: "100%" }}>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
              What kept you from subscribing?
            </FormLabel>
            <RadioGroup
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {REASONS.map((r) => (
                <FormControlLabel
                  key={r.value}
                  value={r.value}
                  control={<Radio />}
                  label={r.label}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <TextField
            label="What were you hoping Snack'd would do that it didn't?"
            placeholder="Optional - share any additional thoughts..."
            multiline
            rows={3}
            fullWidth
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            helperText={`${feedback.length}/2000`}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={!reason || submitting}
            size="large"
          >
            {submitting ? "Submitting..." : "Submit Feedback"}
          </Button>
        </form>
      </Box>
    </Container>
  );
};

export default TrialSurvey;
